var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/versioned-mongo-entity.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import { ID } from "@adornis/baseql/baseqlTypes.js";
import { Arg, Entity, Field, Query } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { context } from "@adornis/baseql/server/context.js";
import "@adornis/users/db/a-user.js";
import { compare } from "fast-json-patch/index.mjs";
import { DateTime } from "luxon";
var LastEditorInfo = class extends AdornisEntity {
};
LastEditorInfo._class = "LastEditorInfos";
__decorateClass([
  Field((type) => ID)
], LastEditorInfo.prototype, "authorID", 2);
__decorateClass([
  Field((type) => DateTime)
], LastEditorInfo.prototype, "timestamp", 2);
LastEditorInfo = __decorateClass([
  Entity()
], LastEditorInfo);
var SinglePatch = class extends AdornisEntity {
  static resolveID(entity) {
    return A.getGloballyUniqueID();
  }
};
SinglePatch._class = "SinglePatch";
__decorateClass([
  Field((type) => String)
], SinglePatch.prototype, "op", 2);
__decorateClass([
  Field((type) => String)
], SinglePatch.prototype, "path", 2);
__decorateClass([
  Field((type) => String)
], SinglePatch.prototype, "value", 2);
SinglePatch = __decorateClass([
  Entity()
], SinglePatch);
var EntityPatch = class extends AdornisEntity {
  static resolveID(entity) {
    return A.getGloballyUniqueID();
  }
  static fromRaw(rawDBObj) {
    return new EntityPatch({
      ...rawDBObj,
      patches: rawDBObj.patches?.map((patch) => new SinglePatch({ ...patch, value: JSON.stringify(patch.value) })) ?? []
    });
  }
  static patchViewList(referenceIDs, collectionName = "entityversions") {
    return async (gqlFields) => {
      const entityVersions = await (await getRawCollection(collectionName)).aggregate([
        { $match: { referenceID: { $in: referenceIDs } } },
        { $addFields: { author: { $first: "$author" } } },
        { $sort: { timestamp: -1 } }
      ]).toArray();
      return entityVersions.map((entityVersion) => EntityPatch.fromRaw(entityVersion));
    };
  }
  static getLastEditor(referenceIDs, collectionName = "entityversions") {
    return async (gqlFields) => {
      const col = await getRawCollection(collectionName);
      const results = await col.aggregate([{ $match: { referenceID: { $in: referenceIDs } } }, { $sort: { timestamp: -1 } }]).toArray();
      const authorID = results[0]?.authorID;
      const timestamp = DateTime.fromJSDate(results[0]?.timestamp);
      return new LastEditorInfo({ authorID, timestamp });
    };
  }
  static async getAuthorName(authorID, collectionName) {
    const col = await getRawCollection(collectionName);
    const result = await col.findOne({ _id: authorID });
    return result?.username;
  }
};
EntityPatch._class = "EntityPatch";
__decorateClass([
  Field((type) => ID)
], EntityPatch.prototype, "authorID", 2);
__decorateClass([
  Field((type) => DateTime)
], EntityPatch.prototype, "timestamp", 2);
__decorateClass([
  Field((type) => ID)
], EntityPatch.prototype, "referenceID", 2);
__decorateClass([
  Field((type) => String)
], EntityPatch.prototype, "referenceClass", 2);
__decorateClass([
  Field((type) => [SinglePatch], { default: (v) => v ?? [] })
], EntityPatch.prototype, "patches", 2);
__decorateClass([
  Field((type) => String, {
    resolve: function() {
      return async (gqlFields) => {
        const { versionedEntityConfig } = await import("../config.js");
        return EntityPatch.getAuthorName(this.authorID, versionedEntityConfig.get("authorCollectionName"));
      };
    }
  })
], EntityPatch.prototype, "authorName", 2);
__decorateClass([
  Query((type) => [EntityPatch], { requestHandlerMeta: { queryCache: { cachePolicy: "bypass" } } }),
  __decorateParam(0, Arg("referenceID", (type) => [String])),
  __decorateParam(1, Arg("collectionName", (type) => String))
], EntityPatch, "patchViewList", 1);
__decorateClass([
  Query((type) => LastEditorInfo),
  __decorateParam(0, Arg("referenceID", (type) => [String])),
  __decorateParam(1, Arg("collectionName", (type) => String))
], EntityPatch, "getLastEditor", 1);
EntityPatch = __decorateClass([
  Entity()
], EntityPatch);
function containsPath(obj, path) {
  const pathArray = path.substring(1).split("/");
  let current = obj;
  for (const part of pathArray) {
    if (current[part] === void 0)
      return false;
    current = current[part];
  }
  return true;
}
async function createVersion(oldEntity, newEntity, collectionName, ignoreFields, customName) {
  const referenceEntity = newEntity ?? oldEntity;
  if (!referenceEntity) {
    logger.error("Create version was called providing neither an oldEntity nor a newEntity!");
    return;
  }
  const patches = compare(
    JSON.parse(JSON.stringify(oldEntity ?? {})),
    JSON.parse(JSON.stringify(newEntity ?? {})),
    true
  );
  const filteredPatches = patches.filter((patch) => {
    if (ignoreFields) {
      return !containsPath(ignoreFields, patch.path);
    }
    return true;
  });
  if (filteredPatches.length === 0)
    return;
  await (await getRawCollection(collectionName)).insertOne({
    _id: A.getGloballyUniqueID(),
    /**
     * When the versioned object is a user that is just being created, we can't yet find a user in the context.
     * In this case we take the _id of the newly created user as the author (of themself).
     */
    authorID: context.serverContext ? "SERVER" : context.userID ?? referenceEntity._id,
    timestamp: /* @__PURE__ */ new Date(),
    referenceID: referenceEntity._id,
    referenceClass: customName ?? referenceEntity._class,
    patches: filteredPatches
  });
}
function Versioned({
  versionCollection = "entityversions",
  ignoreFields = void 0,
  customName = void 0
} = {}) {
  return (TargetClass) => {
    if (baseqlMetaData.entities.validSubclasses(MongoEntity._class).includes(TargetClass._class)) {
      const mongoEntityTarget = TargetClass;
      const targetAfterUpdate = mongoEntityTarget.afterUpdate;
      targetAfterUpdate.skip_version = true;
      mongoEntityTarget.afterUpdate = async (oldEntity, newEntity, result) => {
        const className = customName ? typeof customName === "string" ? customName : customName(newEntity) : oldEntity._class;
        if (!result.skip_version) {
          await createVersion(oldEntity, newEntity, versionCollection, ignoreFields, className);
          result.skip_version = true;
        }
        if (Object.getOwnPropertyNames(mongoEntityTarget).includes("afterUpdate"))
          return targetAfterUpdate.bind(TargetClass)(oldEntity, newEntity, result);
        return Object.getPrototypeOf(mongoEntityTarget).afterUpdate.bind(TargetClass)(oldEntity, newEntity, result);
      };
      mongoEntityTarget.afterUpdate.versioned = true;
      const targetAfterCreate = mongoEntityTarget.afterCreate;
      mongoEntityTarget.afterCreate = async (entity, result) => {
        if (!result.skip_version) {
          await createVersion(null, entity, versionCollection, ignoreFields);
          result.skip_version = true;
        }
        if (Object.getOwnPropertyNames(mongoEntityTarget).includes("afterCreate"))
          return targetAfterCreate.bind(TargetClass)(entity, result);
        return Object.getPrototypeOf(mongoEntityTarget).afterCreate.bind(TargetClass)(entity, result);
      };
      const targetAfterRemove = mongoEntityTarget.afterRemove;
      mongoEntityTarget.afterRemove = async (id, entityData) => {
        const skipVersion = entityData.skip_version;
        delete entityData.skip_version;
        const oldEntity = constructValue(entityData);
        if (!skipVersion) {
          await createVersion(oldEntity, null, versionCollection, ignoreFields);
          entityData.skip_version = true;
        }
        if (Object.getOwnPropertyNames(mongoEntityTarget).includes("afterRemove"))
          return targetAfterRemove.bind(TargetClass)(id, entityData);
        return Object.getPrototypeOf(mongoEntityTarget).afterRemove.bind(TargetClass)(id, entityData);
      };
    } else
      throw new Error("Cannot create entity versions for AdornisEntity!");
  };
}
export {
  EntityPatch,
  LastEditorInfo,
  SinglePatch,
  Versioned,
  createVersion
};
